import React, { useState, useEffect } from 'react';
import { ref, onValue } from 'firebase/database';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import ProductGrid from './ProductGrid';
import Navbar from './Navbar';
import Footer from './Footer';
import { FaFilter, FaTimes, FaSearch } from 'react-icons/fa';
import { BsChevronDown, BsClock } from 'react-icons/bs';

const AccessoriesPage = () => {
  const [accessories, setAccessories] = useState([]);
  const [filteredAccessories, setFilteredAccessories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [priceRange, setPriceRange] = useState([0, 50000]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]); // Wall, Table, Floor, etc.
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedMovementTypes, setSelectedMovementTypes] = useState([]); // Quartz, Mechanical, Digital
  const [selectedStyles, setSelectedStyles] = useState([]); // Modern, Vintage, Classic, etc.
  const [selectedSizes, setSelectedSizes] = useState([]); // Small, Medium, Large
  const [sortOption, setSortOption] = useState('featured');

  // Available filter options
  const [allBrands, setAllBrands] = useState([]);
  const [allTypes, setAllTypes] = useState([]);
  const [allMaterials, setAllMaterials] = useState([]);
  const [allColors, setAllColors] = useState([]);
  const [allMovementTypes, setAllMovementTypes] = useState([]);
  const [allStyles, setAllStyles] = useState([]);
  const [allSizes, setAllSizes] = useState([]);

  // Sophisticated color palette
  const colors = {
    primary: '#1a365d',
    secondary: '#b7791f',
    background: '#f5f7fa',
    panelBg: 'rgba(255, 255, 255, 0.9)',
    textDark: '#2d3748',
    textMuted: '#718096',
    accent: '#c53030',
    border: 'rgba(203, 213, 224, 0.5)',
    highlight: 'rgba(26, 54, 93, 0.1)',
  };

  useEffect(() => {
    const productsRef = ref(db, 'Watch/admin/products');
    const unsubscribe = onValue(productsRef, (snapshot) => {
      const productsData = snapshot.val();
      if (productsData) {
        const accessoriesList = Object.entries(productsData)
          .map(([id, data]) => ({ id, ...data }))
          .filter(product => product.category === 'Clocks');
        
        setAccessories(accessoriesList);

        // Extract unique filter options
        const uniqueBrands = [...new Set(accessoriesList.map(a => a.brand).filter(Boolean))];
        setAllBrands(uniqueBrands);

        const uniqueTypes = [...new Set(
          accessoriesList
            .map(a => a.specifications?.type || a.specifications?.clockType)
            .filter(t => t && typeof t === 'string')
        )];
        setAllTypes(uniqueTypes);

        const uniqueMaterials = [...new Set(
          accessoriesList.flatMap(a => {
            const materials = [];
            if (Array.isArray(a.specifications?.materials)) {
              materials.push(...a.specifications.materials.filter(m => m && typeof m === 'string'));
            } else if (a.specifications?.material && typeof a.specifications.material === 'string') {
              materials.push(a.specifications.material);
            }
            return materials;
          })
        )];
        setAllMaterials(uniqueMaterials);

        const uniqueColors = [...new Set(
          accessoriesList.flatMap(a => {
            const colors = [];
            if (Array.isArray(a.specifications?.colors)) {
              colors.push(...a.specifications.colors.filter(c => c && typeof c === 'string'));
            } else if (a.specifications?.color && typeof a.specifications.color === 'string') {
              colors.push(a.specifications.color);
            }
            return colors.map(c => c.toLowerCase());
          }).filter(Boolean)
        )];
        setAllColors(uniqueColors);

        const uniqueMovementTypes = [...new Set(
          accessoriesList
            .map(a => a.specifications?.movementType || a.specifications?.movement)
            .filter(m => m && typeof m === 'string')
        )];
        setAllMovementTypes(uniqueMovementTypes);

        const uniqueStyles = [...new Set(
          accessoriesList
            .map(a => a.specifications?.style)
            .filter(s => s && typeof s === 'string')
        )];
        setAllStyles(uniqueStyles);

        const uniqueSizes = [...new Set(
          accessoriesList
            .map(a => a.specifications?.size)
            .filter(s => s && typeof s === 'string')
        )];
        setAllSizes(uniqueSizes);
      } else {
        setAccessories([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Apply filters
  useEffect(() => {
    let result = [...accessories];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(a =>
        a.name.toLowerCase().includes(term) ||
        (a.brand && a.brand.toLowerCase().includes(term)) ||
        (a.description && a.description.toLowerCase().includes(term))
      );
    }

    // Price filter
    result = result.filter(a => a.price >= priceRange[0] && a.price <= priceRange[1]);

    // Brand filter
    if (selectedBrands.length > 0) {
      result = result.filter(a => a.brand && selectedBrands.includes(a.brand));
    }

    // Type filter
    if (selectedTypes.length > 0) {
      result = result.filter(a => {
        const type = a.specifications?.type || a.specifications?.clockType;
        return type && typeof type === 'string' && selectedTypes.includes(type);
      });
    }

    // Material filter
    if (selectedMaterials.length > 0) {
      result = result.filter(a => {
        const productMaterials = [];
        if (Array.isArray(a.specifications?.materials)) {
          productMaterials.push(...a.specifications.materials.filter(m => m && typeof m === 'string'));
        } else if (a.specifications?.material && typeof a.specifications.material === 'string') {
          productMaterials.push(a.specifications.material);
        }
        return productMaterials.some(material => selectedMaterials.includes(material));
      });
    }

    // Color filter
    if (selectedColors.length > 0) {
      result = result.filter(a => {
        const productColors = [];
        if (Array.isArray(a.specifications?.colors)) {
          productColors.push(...a.specifications.colors.filter(c => c && typeof c === 'string').map(c => c.toLowerCase()));
        } else if (a.specifications?.color && typeof a.specifications.color === 'string') {
          productColors.push(a.specifications.color.toLowerCase());
        }
        return productColors.some(color => selectedColors.includes(color));
      });
    }

    // Movement type filter
    if (selectedMovementTypes.length > 0) {
      result = result.filter(a => {
        const movementType = a.specifications?.movementType || a.specifications?.movement;
        return movementType && typeof movementType === 'string' && selectedMovementTypes.includes(movementType);
      });
    }

    // Style filter
    if (selectedStyles.length > 0) {
      result = result.filter(a => {
        const style = a.specifications?.style;
        return style && typeof style === 'string' && selectedStyles.includes(style);
      });
    }

    // Size filter
    if (selectedSizes.length > 0) {
      result = result.filter(a => {
        const size = a.specifications?.size;
        return size && typeof size === 'string' && selectedSizes.includes(size);
      });
    }

    // Apply sorting
    switch (sortOption) {
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case 'featured':
        result.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
        break;
      case 'discount':
        result.sort((a, b) => (b.discount || 0) - (a.discount || 0));
        break;
      default:
        break;
    }

    setFilteredAccessories(result);
  }, [accessories, searchTerm, priceRange, selectedBrands, selectedTypes, selectedMaterials, selectedColors, selectedMovementTypes, selectedStyles, selectedSizes, sortOption]);

  const resetFilters = () => {
    setSearchTerm('');
    setPriceRange([0, 50000]);
    setSelectedBrands([]);
    setSelectedTypes([]);
    setSelectedMaterials([]);
    setSelectedColors([]);
    setSelectedMovementTypes([]);
    setSelectedStyles([]);
    setSelectedSizes([]);
    setSortOption('featured');
  };

  const toggleFilter = (filterArray, setFilterArray, value) => {
    setFilterArray(prev =>
      prev.includes(value) ? prev.filter(item => item !== value) : [...prev, value]
    );
  };

  // Helper function to get color hex value
  const getColorValue = (colorName) => {
    const colorMap = {
      'black': '#1a1a1a',
      'white': '#ffffff',
      'red': '#e74c3c',
      'blue': '#3498db',
      'green': '#27ae60',
      'yellow': '#f1c40f',
      'silver': '#bdc3c7',
      'gold': '#f39c12',
      'rose gold': '#e8b4b8',
      'navy': '#2c3e50',
      'brown': '#8b4513',
      'gray': '#7f8c8d',
      'grey': '#7f8c8d',
      'pink': '#e91e63',
      'purple': '#9b59b6',
      'orange': '#e67e22',
      'beige': '#f5f5dc',
      'teal': '#1abc9c',
      'burgundy': '#8e2a2a',
      'tan': '#d2b48c',
      'olive': '#808000',
      'maroon': '#800000',
      'cream': '#fffdd0',
      'ivory': '#fffff0',
      'bronze': '#cd7f32',
      'copper': '#b87333',
      'walnut': '#773f1a',
      'mahogany': '#c04000'
    };
    return colorMap[colorName?.toLowerCase()] || colorName;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: colors.background,
        fontSize: '1.5rem',
        fontWeight: '500',
        color: colors.primary
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            width: '30px',
            height: '30px',
            border: '4px solid #edf2f7',
            borderTop: `4px solid ${colors.primary}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          Loading Timepieces...
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: colors.background,
      minHeight: '100vh',
      paddingBottom: '60px'
    }}>
      <Navbar />
      
      <div style={{
        maxWidth: '1500px',
        margin: '0 auto',
        padding: '60px 20px 0'
      }}>
        {/* Hero Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '60px',
          padding: '0 20px'
        }}>
          <div style={{
            display: 'inline-block',
            background: colors.primary,
            color: 'white',
            padding: '8px 24px',
            borderRadius: '30px',
            fontSize: '0.9rem',
            fontWeight: '600',
            marginBottom: '24px',
            letterSpacing: '1px',
            boxShadow: `0 4px 6px ${colors.highlight}`
          }}>
            PRECISION TIMEPIECES
          </div>
          <h1 style={{
            fontSize: '2.8rem',
            fontWeight: '700',
            color: colors.primary,
            margin: '0 0 16px 0',
            lineHeight: '1.2'
          }}>
            Elegant <span style={{ color: colors.secondary }}>Horology</span>
          </h1>
          <p style={{
            color: colors.textMuted,
            fontSize: '1.1rem',
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: '1.6'
          }}>
            Discover our collection of meticulously crafted clocks that embody timeless elegance
          </p>
        </div>

        {/* Filter Controls */}
        {accessories.length > 0 && (
          <div style={{
            maxWidth: '1400px',
            margin: '0 auto 40px',
            padding: '0 20px'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {/* Search Bar */}
              <div style={{ position: 'relative', maxWidth: '600px' }}>
                <FaSearch style={{
                  position: 'absolute',
                  top: '50%',
                  left: '15px',
                  transform: 'translateY(-50%)',
                  color: colors.textMuted,
                  fontSize: '1rem'
                }} />
                <input
                  type="text"
                  placeholder="Search clocks by name, brand..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 15px 12px 45px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    background: 'white',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'all 0.3s ease',
                    color: colors.textDark
                  }}
                />
              </div>

              {/* Controls Row */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                flexWrap: 'wrap'
              }}>
                {/* Sort Select */}
                <div style={{ position: 'relative' }}>
                  <select
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                    style={{
                      padding: '12px 40px 12px 15px',
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      background: 'white',
                      fontSize: '1rem',
                      appearance: 'none',
                      outline: 'none',
                      cursor: 'pointer',
                      minWidth: '200px',
                      color: colors.textDark
                    }}
                  >
                    <option value="featured">✨ Featured</option>
                    <option value="price-low">💰 Price: Low to High</option>
                    <option value="price-high">💎 Price: High to Low</option>
                    <option value="newest">🆕 Newest Arrivals</option>
                    <option value="discount">🔥 Best Discounts</option>
                  </select>
                  <BsChevronDown style={{
                    position: 'absolute',
                    right: '15px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: colors.textMuted
                  }} />
                </div>

                {/* Filter Button */}
                <button
                  onClick={() => setShowFilters(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 20px',
                    background: colors.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <FaFilter /> Filters
                </button>

                {/* Results Count */}
                <span style={{ color: colors.textMuted, fontSize: '0.95rem', marginLeft: 'auto' }}>
                  {filteredAccessories.length} {filteredAccessories.length === 1 ? 'timepiece' : 'timepieces'} found
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Filter Panel */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: '400px',
            maxWidth: '90%',
            background: 'white',
            boxShadow: '-10px 0 30px rgba(0,0,0,0.1)',
            transform: showFilters ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
            zIndex: 1000,
            overflowY: 'auto',
            padding: '30px',
            visibility: showFilters ? 'visible' : 'hidden',
            opacity: showFilters ? '1' : '0'
          }}
        >
          {/* Filter Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '30px',
            paddingBottom: '20px',
            borderBottom: `2px solid ${colors.border}`
          }}>
            <h2 style={{
              fontSize: '1.8rem',
              fontWeight: '400',
              color: colors.primary,
              margin: 0
            }}>Filter Clocks</h2>
            <button
              onClick={() => setShowFilters(false)}
              style={{
                padding: '10px',
                border: 'none',
                background: '#f5f5f5',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <FaTimes style={{ fontSize: '1.2rem', color: colors.primary }} />
            </button>
          </div>

          {/* Price Range Filter */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{
              fontSize: '1.1rem',
              fontWeight: '500',
              color: colors.textDark,
              marginBottom: '15px'
            }}>💰 Price Range</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  type="number"
                  value={priceRange[0]}
                  onChange={(e) => setPriceRange([parseInt(e.target.value) || 0, priceRange[1]])}
                  min="0"
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    outline: 'none',
                    color: colors.textDark
                  }}
                />
                <span style={{ color: colors.textMuted }}>to</span>
                <input
                  type="number"
                  value={priceRange[1]}
                  onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value) || 50000])}
                  min={priceRange[0]}
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    outline: 'none',
                    color: colors.textDark
                  }}
                />
              </div>
              <p style={{ textAlign: 'center', color: colors.textMuted, fontSize: '0.95rem', margin: 0 }}>
                ₹{priceRange[0].toLocaleString()} - ₹{priceRange[1].toLocaleString()}
              </p>
            </div>
          </div>

          {/* Brand Filter */}
          {allBrands.length > 0 && (
            <FilterSection
              title="🏷️ Brands"
              options={allBrands}
              selectedOptions={selectedBrands}
              onToggle={(value) => toggleFilter(selectedBrands, setSelectedBrands, value)}
              colors={colors}
            />
          )}

          {/* Clock Type Filter */}
          {allTypes.length > 0 && (
            <FilterSection
              title="⏰ Clock Type"
              options={allTypes}
              selectedOptions={selectedTypes}
              onToggle={(value) => toggleFilter(selectedTypes, setSelectedTypes, value)}
              colors={colors}
            />
          )}

          {/* Movement Type Filter */}
          {allMovementTypes.length > 0 && (
            <FilterSection
              title="⚙️ Movement Type"
              options={allMovementTypes}
              selectedOptions={selectedMovementTypes}
              onToggle={(value) => toggleFilter(selectedMovementTypes, setSelectedMovementTypes, value)}
              colors={colors}
            />
          )}

          {/* Style Filter */}
          {allStyles.length > 0 && (
            <FilterSection
              title="✨ Style"
              options={allStyles}
              selectedOptions={selectedStyles}
              onToggle={(value) => toggleFilter(selectedStyles, setSelectedStyles, value)}
              colors={colors}
            />
          )}

          {/* Size Filter */}
          {allSizes.length > 0 && (
            <FilterSection
              title="📏 Size"
              options={allSizes}
              selectedOptions={selectedSizes}
              onToggle={(value) => toggleFilter(selectedSizes, setSelectedSizes, value)}
              colors={colors}
            />
          )}

          {/* Material Filter */}
          {allMaterials.length > 0 && (
            <FilterSection
              title="🧵 Material"
              options={allMaterials}
              selectedOptions={selectedMaterials}
              onToggle={(value) => toggleFilter(selectedMaterials, setSelectedMaterials, value)}
              colors={colors}
            />
          )}

          {/* Color Filter */}
          {allColors.length > 0 && (
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: '500',
                color: colors.textDark,
                marginBottom: '15px'
              }}>🎨 Color</h3>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '10px' 
              }}>
                {allColors.map(color => {
                  const colorValue = getColorValue(color);
                  const isSelected = selectedColors.includes(color);
                  return (
                    <div 
                      key={color}
                      onClick={() => toggleFilter(selectedColors, setSelectedColors, color)}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: colorValue,
                        border: isSelected 
                          ? `3px solid ${colors.primary}` 
                          : `2px solid ${colors.border}`,
                        cursor: 'pointer',
                        boxShadow: isSelected ? '0 4px 12px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.1)',
                        position: 'relative',
                        transition: 'all 0.3s ease',
                        transform: isSelected ? 'scale(1.1)' : 'scale(1)'
                      }}
                      title={color.charAt(0).toUpperCase() + color.slice(1)}
                    >
                      {isSelected && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          color: ['white', 'yellow', 'beige', 'cream', 'ivory', 'silver'].includes(color) ? '#333' : 'white',
                          fontSize: '16px',
                          fontWeight: 'bold'
                        }}>
                          ✓
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Reset Button */}
          <button
            onClick={() => {
              resetFilters();
              setShowFilters(false);
            }}
            style={{
              width: '100%',
              padding: '15px',
              background: colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              marginTop: '20px',
              transition: 'all 0.3s ease'
            }}
          >
            🔄 Reset All Filters
          </button>
        </div>

        {/* Backdrop */}
        {showFilters && (
          <div
            onClick={() => setShowFilters(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 999
            }}
          />
        )}

        {/* Product Grid */}
        {accessories.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '80px 20px',
            background: colors.panelBg,
            borderRadius: '12px',
            boxShadow: `0 4px 6px ${colors.highlight}`,
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            <div style={{
              fontSize: '5rem',
              marginBottom: '20px',
              color: colors.border
            }}>⏱️</div>
            <h3 style={{
              fontSize: '1.8rem',
              color: colors.primary,
              marginBottom: '15px'
            }}>Collection Coming Soon</h3>
            <p style={{
              color: colors.textMuted,
              fontSize: '1.1rem',
              maxWidth: '500px',
              margin: '0 auto'
            }}>
              We're curating an exceptional selection of timepieces for you.
            </p>
          </div>
        ) : filteredAccessories.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '80px 20px',
            background: colors.panelBg,
            borderRadius: '12px',
            boxShadow: `0 4px 6px ${colors.highlight}`,
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🔍</div>
            <h3 style={{
              fontSize: '1.5rem',
              marginBottom: '15px',
              color: colors.primary
            }}>No Clocks Found</h3>
            <p style={{
              color: colors.textMuted,
              maxWidth: '500px',
              margin: '0 auto 20px',
              lineHeight: '1.6'
            }}>
              Try adjusting your filters or search term to find what you're looking for.
            </p>
            <button
              onClick={resetFilters}
              style={{
                padding: '12px 30px',
                background: colors.primary,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <ProductGrid
            products={filteredAccessories}
            loading={loading}
            loadingCount={12}
            showHeader={false}
            gridConfig={{
              columns: {
                mobile: 'repeat(auto-fit, minmax(280px, 1fr))',
                tablet: 'repeat(auto-fit, minmax(320px, 1fr))',
                desktop: 'repeat(auto-fit, minmax(350px, 1fr))'
              },
              gap: {
                mobile: '16px',
                tablet: '24px',
                desktop: '28px'
              }
            }}
            cardStyle={{
              background: colors.panelBg,
              border: `1px solid ${colors.border}`,
              boxShadow: `0 8px 24px ${colors.highlight}`
            }}
          />
        )}
      </div>

      {/* Global styles for animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <Footer />
    </div>
  );
};

// Filter Section Component
const FilterSection = ({ title, options, selectedOptions, onToggle, colors }) => (
  <div style={{ marginBottom: '30px' }}>
    <h3 style={{
      fontSize: '1.1rem',
      fontWeight: '500',
      color: colors.textDark,
      marginBottom: '15px'
    }}>{title}</h3>
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      maxHeight: '200px',
      overflowY: 'auto'
    }}>
      {options.map(option => (
        <label
          key={option}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px',
            borderRadius: '6px',
            cursor: 'pointer',
            background: selectedOptions.includes(option) ? '#f5f5f5' : 'transparent',
            transition: 'all 0.3s ease'
          }}
        >
          <input
            type="checkbox"
            checked={selectedOptions.includes(option)}
            onChange={() => onToggle(option)}
            style={{
              width: '18px',
              height: '18px',
              cursor: 'pointer',
              accentColor: colors.primary
            }}
          />
          <span style={{ fontSize: '0.95rem', color: colors.textDark }}>
            {option.charAt(0).toUpperCase() + option.slice(1)}
          </span>
        </label>
      ))}
    </div>
  </div>
);

export default AccessoriesPage;
